import { useMemo, useRef, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { useNavigate } from '@tanstack/react-router'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  UserRoundCog,
  Users,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  useCreateEmployee,
  useDeleteEmployee,
  useEmployees,
  useImportEmployees,
  useUpdateEmployee,
} from '@/features/employees/hooks'
import { useCreateEmployeeLogin } from '@/features/users/hooks'
import { OneTimePasswordDialog } from '@/features/users/components/one-time-password-dialog'
import { useRoles } from '@/features/roles/hooks'
import { downloadFile } from '@/lib/download'
import type { Employee } from '@/types/employee'

const DEPARTMENT_OPTIONS = ['Sales', 'Service', 'Admin', 'Dock & Yard']
// Radix Select can't hold an empty value, so this sentinel stands in for "".
const DEPARTMENT_NONE = 'none'

// Client-side mirror of the backend's username derivation (first initial +
// last name, e.g. "Marisol Vega" → "mvega"). Only used for the placeholder —
// the server derives and dedupes whenever the field is left blank.
function suggestUsername(name: string): string {
  const fields = name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
  if (fields.length === 0) return ''
  return fields.length > 1 ? fields[0][0] + fields[fields.length - 1] : fields[0]
}

// Click-to-sort header: cycles asc -> desc -> unsorted, with the arrow showing
// the current direction. Same pattern as the inventory boat table.
function SortableHeader({
  column,
  children,
}: {
  column: Column<Employee, unknown>
  children: React.ReactNode
}) {
  const sorted = column.getIsSorted()
  return (
    <Button variant="ghost" onClick={() => column.toggleSorting(sorted === 'asc')}>
      {children}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-2 h-4 w-4" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-2 h-4 w-4" />
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  )
}

// Hired/terminated dates are YYYY-MM-DD, so string sort == chronological.
// Status sorts actives ("") before leavers (by termination date).
function employeeColumns({
  onEdit,
  onDelete,
  onCreateLogin,
  onManageLogin,
}: {
  onEdit: (employee: Employee) => void
  onDelete: (employee: Employee) => void
  onCreateLogin: (employee: Employee) => void
  onManageLogin: () => void
}): ColumnDef<Employee>[] {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'department',
      header: ({ column }) => <SortableHeader column={column}>Department</SortableHeader>,
      cell: ({ row }) => <span className="text-sm">{row.original.department || '—'}</span>,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <SortableHeader column={column}>Email</SortableHeader>,
      cell: ({ row }) => <span className="text-sm">{row.original.email || '—'}</span>,
    },
    {
      accessorKey: 'hired_on',
      header: ({ column }) => <SortableHeader column={column}>Hired</SortableHeader>,
      cell: ({ row }) => <span className="text-sm">{row.original.hired_on}</span>,
    },
    {
      id: 'status',
      accessorFn: (employee) => employee.terminated_on ?? '',
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      cell: ({ row }) =>
        row.original.terminated_on ? (
          <Badge variant="destructive">left {row.original.terminated_on}</Badge>
        ) : (
          <Badge variant="outline">active</Badge>
        ),
    },
    {
      id: 'login',
      accessorFn: (employee) => employee.username ?? '',
      header: ({ column }) => <SortableHeader column={column}>Login</SortableHeader>,
      cell: ({ row }) =>
        row.original.username ? (
          <Badge variant="secondary">@{row.original.username}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    {
      id: 'actions',
      enableSorting: false,
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(row.original)}>
            <Pencil className="h-4 w-4" />
          </Button>
          {row.original.user_id ? (
            <Button
              variant="ghost"
              size="icon"
              title="Manage login (opens the Users tab)"
              onClick={onManageLogin}
            >
              <UserRoundCog className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              title={
                row.original.terminated_on
                  ? 'Terminated employees cannot get a login'
                  : 'Create a login for this employee'
              }
              disabled={!!row.original.terminated_on}
              onClick={() => onCreateLogin(row.original)}
            >
              <KeyRound className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            title="Delete employee"
            onClick={() => onDelete(row.original)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ]
}

interface EmployeeFormState {
  name: string
  department: string
  email: string
  hired_on: string
  terminated_on: string // "" = currently employed
}

const emptyEmployeeForm: EmployeeFormState = {
  name: '',
  department: '',
  email: '',
  hired_on: '',
  terminated_on: '',
}

// The "create login" dialog: role is required, username blank = derived from
// the name on the server, email pre-filled from the roster.
interface LoginFormState {
  role_id: string
  username: string
  email: string
}

export function EmployeesTab() {
  const { data: employees, isLoading, isError } = useEmployees()
  const { data: roles } = useRoles()
  const navigate = useNavigate()
  const [sorting, setSorting] = useState<SortingState>([])
  const [showInactive, setShowInactive] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Employee | null>(null)
  const [form, setForm] = useState<EmployeeFormState>(emptyEmployeeForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<Employee | null>(null)
  const [loginFor, setLoginFor] = useState<Employee | null>(null)
  const [loginForm, setLoginForm] = useState<LoginFormState>({ role_id: '', username: '', email: '' })
  const [loginError, setLoginError] = useState<string | null>(null)
  const [loginResult, setLoginResult] = useState<{ username: string; password: string } | null>(
    null,
  )
  const [importError, setImportError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ created: number; updated: number } | null>(
    null,
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  const createEmployee = useCreateEmployee()
  const updateEmployee = useUpdateEmployee()
  const deleteEmployee = useDeleteEmployee()
  const importEmployees = useImportEmployees()
  const createLogin = useCreateEmployeeLogin()
  const saving = createEmployee.isPending || updateEmployee.isPending

  // Memoized identity matters: TanStack Table resets pagination state
  // whenever `data` changes identity, and a new array each render would
  // re-trigger that on every render (infinite "Maximum update depth" loop).
  const visibleEmployees = useMemo(
    () =>
      showInactive
        ? employees ?? []
        : (employees ?? []).filter((employee) => !employee.terminated_on),
    [employees, showInactive],
  )

  // Keep an out-of-list department (CSV import, legacy seed values) selectable
  // so editing such an employee doesn't silently clear it.
  const departmentChoices =
    form.department && !DEPARTMENT_OPTIONS.includes(form.department)
      ? [...DEPARTMENT_OPTIONS, form.department]
      : DEPARTMENT_OPTIONS

  const openCreate = () => {
    setEditing(null)
    setForm(emptyEmployeeForm)
    setFormError(null)
    setDialogOpen(true)
  }

  const openEdit = (employee: Employee) => {
    setEditing(employee)
    setForm({
      name: employee.name,
      department: employee.department,
      email: employee.email ?? '',
      hired_on: employee.hired_on,
      terminated_on: employee.terminated_on ?? '',
    })
    setFormError(null)
    setDialogOpen(true)
  }

  const openCreateLogin = (employee: Employee) => {
    setLoginFor(employee)
    setLoginForm({
      role_id: roles?.find((r) => !r.is_system)?.id ?? '',
      username: '',
      email: employee.email ?? '',
    })
    setLoginError(null)
  }

  const onCreateLogin = () => {
    if (!loginFor) return
    setLoginError(null)
    if (!loginForm.role_id) {
      setLoginError('Pick a role for the login.')
      return
    }
    createLogin.mutate(
      {
        employee_id: loginFor.id,
        role_id: loginForm.role_id,
        // Blank username = the server derives and dedupes one from the name.
        username: loginForm.username.trim() || undefined,
        email: loginForm.email.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          setLoginFor(null)
          setLoginResult({ username: data.user.username, password: data.password })
        },
        onError: (err) => setLoginError(err.message),
      },
    )
  }

  const onManageLogin = () => {
    void navigate({ to: '/settings/account', search: { tab: 'users' } })
  }

  const columns = employeeColumns({
    onEdit: openEdit,
    onDelete: setDeleting,
    onCreateLogin: openCreateLogin,
    onManageLogin: onManageLogin,
  })

  const table = useReactTable({
    data: visibleEmployees,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  })

  const onSave = () => {
    setFormError(null)
    if (!form.name.trim()) {
      setFormError('Give the employee a name.')
      return
    }
    if (!form.hired_on) {
      setFormError('Pick a hire date.')
      return
    }
    const body = {
      name: form.name.trim(),
      department: form.department,
      email: form.email.trim() || undefined,
      hired_on: form.hired_on,
      terminated_on: form.terminated_on || undefined,
    }
    const opts = {
      onSuccess: () => setDialogOpen(false),
      onError: (err: Error) => setFormError(err.message),
    }
    if (editing) {
      updateEmployee.mutate({ id: editing.id, body }, opts)
    } else {
      createEmployee.mutate(body, opts)
    }
  }

  const onDelete = () => {
    if (!deleting) return
    deleteEmployee.mutate(deleting.id, {
      onSuccess: () => setDeleting(null),
    })
  }

  const onPickFile = () => {
    setImportError(null)
    fileInputRef.current?.click()
  }

  const onFileChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-uploading the same file
    if (!file) return
    setImportError(null)
    setImportResult(null)
    importEmployees.mutate(file, {
      onSuccess: (result) => setImportResult(result),
      onError: (err) => setImportError(err.message),
    })
  }

  const onDownload = () => downloadFile('/employees/export', { filename: 'employees.csv' })

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Employees
            </CardTitle>
            <CardDescription>
              The staff roster — sales people, service techs, and everyone else. Feeds the turnover
              and revenue-per-employee KPIs.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onDownload} title="Download the roster as CSV">
              <Download className="mr-2 h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" onClick={onPickFile} disabled={importEmployees.isPending}>
              {importEmployees.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Upload CSV
            </Button>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> New employee
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFileChosen}
          />
          {(importError || importResult) && (
            <div className="mb-3">
              {importError && <p className="text-destructive text-sm">{importError}</p>}
              {importResult && (
                <p className="text-muted-foreground text-sm">
                  Imported {importResult.created + importResult.updated} employee
                  {importResult.created + importResult.updated === 1 ? '' : 's'} —{' '}
                  {importResult.created} new, {importResult.updated} updated.
                </p>
              )}
            </div>
          )}
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="text-destructive text-sm">Failed to load employees.</p>
          ) : (
            <>
              {!!employees?.length && (
                <label
                  htmlFor="show-inactive"
                  className="mb-3 flex cursor-pointer items-center gap-2 text-sm"
                >
                  <Checkbox
                    id="show-inactive"
                    checked={showInactive}
                    onCheckedChange={(checked) => setShowInactive(checked === true)}
                  />
                  Show non-active employees
                </label>
              )}
              {!employees?.length ? (
                <p className="text-muted-foreground py-6 text-center text-sm">
                  No employees yet — add one or upload a CSV.
                </p>
              ) : visibleEmployees.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-sm">
                  No active employees — turn on “Show non-active employees” to see former staff.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={header.column.id === 'actions' ? 'text-right' : undefined}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New employee'}</DialogTitle>
            <DialogDescription>
              Roster members feed the headcount and turnover KPIs. A termination date marks when
              someone left.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label htmlFor="e-name">Name</Label>
              <Input
                id="e-name"
                value={form.name}
                placeholder="e.g. R. Mendez"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="e-email">Email (optional)</Label>
              <Input
                id="e-email"
                type="email"
                value={form.email}
                placeholder="used to prefill a login"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="e-department">Department (optional)</Label>
              <Select
                value={form.department === '' ? DEPARTMENT_NONE : form.department}
                onValueChange={(value) =>
                  setForm({ ...form, department: value === DEPARTMENT_NONE ? '' : value })
                }
              >
                <SelectTrigger id="e-department">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEPARTMENT_NONE}>None</SelectItem>
                  {departmentChoices.map((department) => (
                    <SelectItem key={department} value={department}>
                      {department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="e-hired">Hired on</Label>
                <Input
                  id="e-hired"
                  type="date"
                  value={form.hired_on}
                  onChange={(e) => setForm({ ...form, hired_on: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="e-terminated">Terminated on (optional)</Label>
                <Input
                  id="e-terminated"
                  type="date"
                  value={form.terminated_on}
                  onChange={(e) => setForm({ ...form, terminated_on: e.target.value })}
                />
              </div>
            </div>
            {formError && <p className="text-destructive text-sm">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Save changes' : 'Add employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>
              This permanently removes them from the roster. Past headcount and turnover
              calculations are affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={deleteEmployee.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={deleteEmployee.isPending}>
              {deleteEmployee.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!loginFor} onOpenChange={(open) => !open && setLoginFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a login for {loginFor?.name}</DialogTitle>
            <DialogDescription>
              A one-time password is generated for you to hand over — no password to invent. Manage
              the login later from the Users tab.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select
                value={loginForm.role_id}
                onValueChange={(v) => setLoginForm({ ...loginForm, role_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {(roles ?? []).map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                      {role.is_system ? ' (full access)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="e-login-username">Username (optional)</Label>
              <Input
                id="e-login-username"
                value={loginForm.username}
                placeholder={`auto — ${suggestUsername(loginFor?.name ?? '')}`}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              />
              <p className="text-muted-foreground text-xs">
                Leave blank to derive one from their name.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="e-login-email">Email (optional)</Label>
              <Input
                id="e-login-email"
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              />
              <p className="text-muted-foreground text-xs">
                Enables password reset by email and Google sign-in matching.
              </p>
            </div>
            {loginError && <p className="text-destructive text-sm">{loginError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoginFor(null)} disabled={createLogin.isPending}>
              Cancel
            </Button>
            <Button onClick={onCreateLogin} disabled={createLogin.isPending}>
              {createLogin.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create login
              <KeyRound className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loginResult && (
        <OneTimePasswordDialog
          password={loginResult.password}
          title={`Temporary password for @${loginResult.username}`}
          description="Share it through a secure channel — it is shown only once and they should change it after signing in."
          onClose={() => setLoginResult(null)}
        />
      )}
    </>
  )
}
